import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Home, Building2, Map, MapPin, Users, ChevronRight, RefreshCw, AlertCircle, Download } from 'lucide-react';
import { territorialAPI, CETerritory, Circle, District, Division, SubDivision, Mandal } from '../services/territorial-api.service';
import { worksApiService, useWorksApi } from '../services/workApi.service';
import { WorkSummary, DistrictSummary, WorkStage } from '../types/work.types';
import * as XLSX from 'xlsx';

// Type Definitions
type TerritorialLevel = 'territories' | 'circles' | 'districts' | 'divisions' | 'sub-divisions' | 'mandals';

interface ChildEntity {
  id: number;
  name: string;
  code: string;
  summary?: WorkSummary;
}

interface CurrentLevelData {
  current_entity: { id: number; name: string; code: string };
  children?: ChildEntity[];
  summary: WorkSummary;
  navigation: {
    can_go_deeper: boolean;
    next_level?: TerritorialLevel;
    breadcrumb: Array<{ level: TerritorialLevel; id: number; name: string }>;
  };
}

interface TerritorialDrilldownProps {
  initialLevel?: TerritorialLevel;
  initialEntityId?: number;
  className?: string;
}

interface Hierarchy {
  territory: { id: number; name: string };
  circle: { id: number; name: string };
  district: { id: number; name: string };
  division: { id: number; name: string };
  subDivision: { id: number; name: string };
  mandal: { id: number; name: string };
}

// Type guard for Hierarchy
const isValidHierarchy = (hierarchy: any): hierarchy is Hierarchy => {
  return (
    hierarchy &&
    typeof hierarchy.territory === 'object' &&
    typeof hierarchy.circle === 'object' &&
    typeof hierarchy.district === 'object' &&
    typeof hierarchy.division === 'object' &&
    typeof hierarchy.subDivision === 'object' &&
    typeof hierarchy.mandal === 'object' &&
    typeof hierarchy.territory.id === 'number' &&
    typeof hierarchy.circle.id === 'number' &&
    typeof hierarchy.district.id === 'number' &&
    typeof hierarchy.division.id === 'number' &&
    typeof hierarchy.subDivision.id === 'number' &&
    typeof hierarchy.mandal.id === 'number' &&
    typeof hierarchy.territory.name === 'string' &&
    typeof hierarchy.circle.name === 'string' &&
    typeof hierarchy.district.name === 'string' &&
    typeof hierarchy.division.name === 'string' &&
    typeof hierarchy.subDivision.name === 'string' &&
    typeof hierarchy.mandal.name === 'string'
  );
};

// Component
const TerritorialDrilldown: React.FC<TerritorialDrilldownProps> = ({
  initialLevel = 'territories',
  initialEntityId = 1,
  className = '',
}) => {
  const { formatCurrency } = useWorksApi();
  const [currentLevel, setCurrentLevel] = useState<TerritorialLevel>(initialLevel);
  const [currentEntityId, setCurrentEntityId] = useState<number>(initialEntityId);
  const [currentData, setCurrentData] = useState<CurrentLevelData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data for the current level
  const loadCurrentLevel = useCallback(async (level: TerritorialLevel, entityId: number) => {
    setLoading(true);
    setError(null);
    try {
      let entity: CETerritory | Circle | District | Division | SubDivision | Mandal;
      let children: ChildEntity[] = [];
      let summary: WorkSummary;

      // Fetch entity and children based on level
      switch (level) {
        case 'territories':
          entity = await territorialAPI.getCETerritoryById(entityId, true);
          children = (await territorialAPI.getCirclesByCETerritory(entityId)).data.map(circle => ({
            id: circle.id,
            name: circle.name,
            code: circle.code,
          }));
          summary = await worksApiService.getDistrictWiseSummary().then(data => ({
            total_works: data.reduce((sum, d) => sum + d.total_works, 0),
            total_estimate_cost: data.reduce((sum, d) => sum + d.total_estimate_cost, 0),
            total_expenditure: data.reduce((sum, d) => sum + d.total_expenditure, 0),
            financial_progress_percentage: data.length
              ? data.reduce((sum, d) => sum + d.financial_progress_percentage, 0) / data.length
              : 0,
            total_ras_amount: data.reduce((sum, d) => sum + (d.total_ras_amount || 0), 0),
            total_length_sanctioned: data.reduce((sum, d) => sum + (d.total_length_sanctioned || 0), 0),
            total_length_completed: data.reduce((sum, d) => sum + (d.total_length_completed || 0), 0),
            total_bills_pending: data.reduce((sum, d) => sum + (d.total_bills_pending || 0), 0),
            total_work_not_recorded: data.reduce((sum, d) => sum + (d.total_work_not_recorded || 0), 0),
            physical_progress_percentage: data.length ? data.reduce((sum, d) => sum + (d.physical_progress_percentage || 0), 0) / data.length : 0,
            avg_estimate_cost: data.length ? data.reduce((sum, d) => sum + d.avg_estimate_cost, 0) / data.length : 0,
            avg_completion_percentage: data.length ? data.reduce((sum, d) => sum + d.physical_progress_percentage, 0) / data.length : 0,
          }));
          break;
        case 'circles':
          entity = await territorialAPI.getCircleById(entityId, true);
          children = (await territorialAPI.getDistrictsByCircle(entityId)).data.map(district => ({
            id: district.id,
            name: district.name,
            code: district.code,
          }));
          summary = await worksApiService.getDistrictWiseSummary().then(data => {
            const districtSummaries = data.filter(d => d.district_id === entityId);
            return districtSummaries.length
              ? {
                  total_works: districtSummaries.reduce((sum, d) => sum + d.total_works, 0),
                  total_estimate_cost: districtSummaries.reduce((sum, d) => sum + d.total_estimate_cost, 0),
                  total_expenditure: districtSummaries.reduce((sum, d) => sum + d.total_expenditure, 0),
                  financial_progress_percentage: districtSummaries.reduce((sum, d) => sum + d.financial_progress_percentage, 0) / districtSummaries.length,
                  total_ras_amount: districtSummaries.reduce((sum, d) => sum + (d.total_ras_amount || 0), 0),
                  total_length_sanctioned: districtSummaries.reduce((sum, d) => sum + (d.total_length_sanctioned || 0), 0),
                  total_length_completed: districtSummaries.reduce((sum, d) => sum + (d.total_length_completed || 0), 0),
                  total_bills_pending: districtSummaries.reduce((sum, d) => sum + (d.total_bills_pending || 0), 0),
                  total_work_not_recorded: districtSummaries.reduce((sum, d) => sum + (d.total_work_not_recorded || 0), 0),
                  physical_progress_percentage: districtSummaries.reduce((sum, d) => sum + (d.physical_progress_percentage || 0), 0) / districtSummaries.length,
                  avg_estimate_cost: districtSummaries.length ? districtSummaries.reduce((sum, d) => sum + d.avg_estimate_cost, 0) / districtSummaries.length : 0,
                  avg_completion_percentage: districtSummaries.length ? districtSummaries.reduce((sum, d) => sum + d.physical_progress_percentage, 0) / districtSummaries.length : 0,
                }
              : {
                  total_works: 0,
                  total_estimate_cost: 0,
                  total_expenditure: 0,
                  financial_progress_percentage: 0,
                  total_ras_amount: 0,
                  total_length_sanctioned: 0,
                  total_length_completed: 0,
                  total_bills_pending: 0,
                  total_work_not_recorded: 0,
                  physical_progress_percentage: 0,
                  avg_estimate_cost: 0,
                  avg_completion_percentage: 0,
                };
          });
          break;
        case 'districts':
          entity = await territorialAPI.getDistrictById(entityId, true);
          children = (await territorialAPI.getDivisionsByDistrict(entityId)).data.map(division => ({
            id: division.id,
            name: division.name,
            code: division.code,
          }));
          summary = await worksApiService.getDistrictWiseSummary().then(data => {
            const districtSummary = data.find(d => d.district_id === entityId) || {
              total_works: 0,
              total_estimate_cost: 0,
              total_expenditure: 0,
              financial_progress_percentage: 0,
              total_ras_amount: 0,
              total_length_sanctioned: 0,
              total_length_completed: 0,
              total_bills_pending: 0,
              total_work_not_recorded: 0,
              physical_progress_percentage: 0,
              avg_estimate_cost: 0,
              avg_completion_percentage: 0,
            };
            return districtSummary;
          });
          break;
        case 'divisions':
          entity = await territorialAPI.getDivisionById(entityId, true);
          children = (await territorialAPI.getSubDivisionsByDivision(entityId)).data.map(subdivision => ({
            id: subdivision.id,
            name: subdivision.name,
            code: subdivision.code,
          }));
          summary = await worksApiService.getWorksByDivision(entityId).then(data => data.summary);
          break;
        case 'sub-divisions':
          entity = await territorialAPI.getSubDivisionById(entityId, true);
          children = (await territorialAPI.getMandalsBySubDivision(entityId)).data.map(mandal => ({
            id: mandal.id,
            name: mandal.name,
            code: mandal.code,
          }));
          summary = await worksApiService.getWorksBySubDivision(entityId).then(data => data.summary);
          break;
        case 'mandals':
          entity = await territorialAPI.getMandalById(entityId, true);
          summary = await worksApiService.getWorksByMandal(entityId).then(data => data.summary);
          break;
        default:
          throw new Error('Invalid territorial level');
      }

      // Construct breadcrumb with explicit typing
      const hierarchy = level === 'mandals' ? await territorialAPI.getFullHierarchy(entityId) : null;
      const breadcrumb: Array<{ level: TerritorialLevel; id: number; name: string }> = hierarchy && isValidHierarchy(hierarchy)
        ? [
            { level: 'territories' as TerritorialLevel, id: hierarchy.territory.id, name: hierarchy.territory.name },
            { level: 'circles' as TerritorialLevel, id: hierarchy.circle.id, name: hierarchy.circle.name },
            { level: 'districts' as TerritorialLevel, id: hierarchy.district.id, name: hierarchy.district.name },
            { level: 'divisions' as TerritorialLevel, id: hierarchy.division.id, name: hierarchy.division.name },
            { level: 'sub-divisions' as TerritorialLevel, id: hierarchy.subDivision.id, name: hierarchy.subDivision.name },
            { level: 'mandals' as TerritorialLevel, id: hierarchy.mandal.id, name: hierarchy.mandal.name },
          ].filter(item => item.level !== level)
        : [{ level, id: entityId, name: entity.name }];

      setCurrentData({
        current_entity: { id: entity.id, name: entity.name, code: entity.code },
        children: level !== 'mandals' ? children : undefined,
        summary,
        navigation: {
          can_go_deeper: level !== 'mandals',
          next_level: level !== 'mandals' ? getNextLevel(level) : undefined,
          breadcrumb,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Failed to load territorial data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Navigation handlers
  const navigateToEntity = useCallback((level: TerritorialLevel, entityId: number) => {
    setCurrentLevel(level);
    setCurrentEntityId(entityId);
    loadCurrentLevel(level, entityId);
  }, [loadCurrentLevel]);

  const navigateToChild = useCallback((child: ChildEntity) => {
    if (!currentData?.navigation?.can_go_deeper || !currentData.navigation.next_level) return;
    navigateToEntity(currentData.navigation.next_level, child.id);
  }, [currentData, navigateToEntity]);

  const navigateToBreadcrumb = useCallback((item: { level: TerritorialLevel; id: number }) => {
    navigateToEntity(item.level, item.id);
  }, [navigateToEntity]);

  // Export data
  const exportData = useCallback(async () => {
    try {
      const exportResult = await worksApiService.exportToExcel({
        [currentLevel]: currentEntityId,
      });
      const worksheet = XLSX.utils.json_to_sheet(exportResult.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Works Summary');
      XLSX.writeFile(workbook, exportResult.filename || `works_${currentLevel}_${currentEntityId}.xlsx`);
    } catch (err) {
      setError('Failed to export data');
      console.error('Export failed:', err);
    }
  }, [currentLevel, currentEntityId]);

  // Helper to get next level
  const getNextLevel = (level: TerritorialLevel): TerritorialLevel | undefined => {
    const levels: TerritorialLevel[] = ['territories', 'circles', 'districts', 'divisions', 'sub-divisions', 'mandals'];
    const currentIndex = levels.indexOf(level);
    return currentIndex < levels.length - 1 ? levels[currentIndex + 1] : undefined;
  };

  // Initial load
  useEffect(() => {
    loadCurrentLevel(currentLevel, currentEntityId);
  }, [currentLevel, currentEntityId, loadCurrentLevel]);

  // Memoized level icon
  const levelIcon = useMemo(() => {
    const icons: Record<TerritorialLevel, React.ComponentType<any>> = {
      territories: Home,
      circles: Building2,
      districts: Map,
      divisions: MapPin,
      'sub-divisions': MapPin,
      mandals: Users,
    };
    return icons[currentLevel] || MapPin;
  }, [currentLevel]);

  // Format number for display
  const formatNumber = (num: number): string => new Intl.NumberFormat('en-IN').format(num);

  // Render components
  const renderBreadcrumb = () => (
    <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6" aria-label="Breadcrumb">
      {currentData?.navigation?.breadcrumb?.map((item, index) => (
        <React.Fragment key={`${item.level}-${item.id}`}>
          {index > 0 && <ChevronRight className="w-4 h-4" />}
          <button
            onClick={() => navigateToBreadcrumb(item)}
            className="flex items-center space-x-1 hover:text-blue-600 transition-colors"
            aria-current={item.level === currentLevel ? 'page' : undefined}
          >
            {React.createElement(levelIcon, { className: 'w-4 h-4' })}
            <span>{item.name}</span>
          </button>
        </React.Fragment>
      ))}
    </nav>
  );

  const renderSummaryCards = () => {
    const summary = currentData?.summary;
    if (!summary) return null;

    const cards = [
      { label: 'Total Works', value: formatNumber(summary.total_works), color: 'blue' },
      { label: 'Total Cost', value: formatCurrency(summary.total_estimate_cost), color: 'green' },
      { label: 'Expenditure', value: formatCurrency(summary.total_expenditure), color: 'purple' },
      { label: 'Progress', value: `${summary.financial_progress_percentage.toFixed(1)}%`, color: 'orange' },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((card, index) => (
          <div
            key={index}
            className={`bg-white p-4 rounded-lg shadow border-l-4 border-${card.color}-500`}
            role="region"
            aria-label={card.label}
          >
            <p className="text-sm text-gray-600">{card.label}</p>
            <p className="text-xl font-semibold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderChildren = () => {
    const children = currentData?.children;
    if (!children || children.length === 0) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" role="list">
        {children.map(child => (
          <button
            key={child.id}
            onClick={() => navigateToChild(child)}
            className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
            aria-label={`Navigate to ${child.name}`}
          >
            <h3 className="text-lg font-semibold text-gray-900">{child.name}</h3>
            <p className="text-sm text-gray-600">Code: {child.code}</p>
            {child.summary && (
              <div className="mt-2 text-sm text-gray-600">
                <p>Works: {formatNumber(child.summary.total_works)}</p>
                <p>Cost: {formatCurrency(child.summary.total_estimate_cost)}</p>
              </div>
            )}
          </button>
        ))}
      </div>
    );
  };

  const renderError = () => (
    <div
      className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2 text-red-700"
      role="alert"
    >
      <AlertCircle className="w-5 h-5" />
      <p>{error}</p>
      <button
        onClick={() => loadCurrentLevel(currentLevel, currentEntityId)}
        className="ml-auto text-sm text-red-600 hover:text-red-800"
        aria-label="Retry loading data"
      >
        <RefreshCw className="w-4 h-4 inline mr-1" />
        Retry
      </button>
    </div>
  );

  return (
    <div className={`p-6 bg-gray-100 min-h-screen ${className}`}>
      {loading && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        {error && renderError()}
        {renderBreadcrumb()}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            {React.createElement(levelIcon, { className: 'w-6 h-6 mr-2' })}
            {currentData?.current_entity.name || 'Loading...'}
          </h2>
          <button
            onClick={exportData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            aria-label="Export data to Excel"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
        {renderSummaryCards()}
        {renderChildren()}
      </div>
    </div>
  );
};

export default TerritorialDrilldown;